import { canv } from "@/canv/global.js";
import {
    generateUID,
    getColorProperty,
    getNumberProperty,
    getStringProperty,
    lightOrDark,
} from "@/canv/utils.js";
import { textWidget } from "@/elements/text/text.js";
import type { TableCellRound, TableWidgetCellFixState, TableWidgetCellStateMap } from "./types";

export class TableWidgetCell {
    constructor() {
        this.UID = generateUID();

        this.textPart = new textWidget(0, 0);

        this.textPart.menuTextSize(17, true);
        this.textPart.shapeMode = true;
        this.textPart.setKeepMinWidth(false);
    }

    updatePosition(startX: number, startY: number) {
        this.startX = startX;
        this.startY = startY;

        if (this.alignment === "left") {
            this.textPart.startX = this.startX + this.horizontalPadding;
            this.textPart.startY =
                this.startY + this.textPart.lines[0].textHeight + this.verticalPadding;
        } else if (this.alignment === "center") {
            this.textPart.startX = this.startX + this.width / 2 - this.textPart.width / 2;
            this.textPart.startY =
                this.startY +
                this.height / 2 -
                this.textPart.height / 2 +
                this.textPart.lines[0].textHeight;
        } else {
            this.textPart.startX =
                this.startX + this.width - this.horizontalPadding - this.textPart.width;
            this.textPart.startY =
                this.startY + this.textPart.lines[0].textHeight + this.verticalPadding;
        }

        this.textPart.updateDims();
    }

    isMergedCell() {
        return this.sectionWidth > 1;
    }

    TryAction(type: string) {
        this.textPart.TryAction(type);
    }

    copyCellOptions(cell: TableWidgetCell) {
        // копируем настройки текста
        this.textPart.fontSize = cell.textPart.fontSize;
        this.textPart.fontStyle = cell.textPart.fontStyle;
        this.textPart.fontColor = cell.textPart.fontColor;
        this.textPart.fontBold = cell.textPart.fontBold;
        this.textPart.fontStrike = cell.textPart.fontStrike;
        this.textPart.menuTextBulleted(cell.textPart.bullettedList, true);
        this.textPart.menuTextAlign(cell.textPart.align, true);

        this.alignment = cell.alignment;
        this.setBackgroundColor(cell.backgroundColor);
        this.sectionWidth = cell.sectionWidth;
    }

    clipCell() {
        canv.ctx.beginPath();
        canv.ctx.moveTo(this.startX, this.startY);
        canv.ctx.lineTo(this.startX + this.width, this.startY);
        canv.ctx.lineTo(this.startX + this.width, this.startY + this.height);
        canv.ctx.lineTo(this.startX, this.startY + this.height);
        canv.ctx.lineTo(this.startX, this.startY);

        canv.ctx.clip();
    }

    drawRoundedRect(x: number, y: number, w: number, h: number, cellRound: TableCellRound) {
        canv.ctx.beginPath();

        canv.ctx.moveTo(x + cellRound["0"], y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, cellRound["1"]);
        canv.ctx.arcTo(x + w, y + h, x, y + h, cellRound["2"]);
        canv.ctx.arcTo(x, y + h, x, y, cellRound["3"]);
        canv.ctx.arcTo(x, y, x + w, y, cellRound["0"]);

        canv.ctx.closePath();
    }

    draw(cellRound: TableCellRound) {
        this.drawRoundedRect(this.startX, this.startY, this.width, this.height, cellRound);
        canv.ctx.fillStyle = this.backgroundColor;
        canv.ctx.strokeStyle = "#AEAEAE";
        canv.ctx.lineWidth = 1;
        canv.ctx.fill();
        canv.ctx.stroke();

        canv.ctx.save();
        this.clipCell();
        this.textPart.draw();
        canv.ctx.restore();
    }

    isOver() {
        const { x, y } = canv.actionManager.getActionPoint();

        return (
            x >= this.startX &&
            x <= this.startX + this.width &&
            y >= this.startY &&
            y <= this.startY + this.height
        );
    }

    clearText() {
        this.textPart.clearText();
    }

    setBackgroundColor(color: string) {
        this.backgroundColor = color;

        if (lightOrDark(this.backgroundColor) === "light") {
            this.textPart.menuTextColor("black", true, false);
        } else {
            this.textPart.menuTextColor("white", true, false);
        }
    }

    getState<T extends TableWidgetCellFixState>(fixType: T): TableWidgetCellStateMap[T] {
        switch (fixType) {
            case "props": // нельзя для widget menu сохранять full, так как сохраняется width
                return {
                    UID: this.UID,
                    backgroundColor: this.backgroundColor,
                    alignment: this.alignment,
                    textPart: this.textPart.getState("full"),
                    sectionWidth: this.sectionWidth || 1,
                } as unknown as TableWidgetCellStateMap[T];
            case "full":
                return {
                    UID: this.UID,
                    width: this.width,
                    height: this.height, // на будущее
                    backgroundColor: this.backgroundColor,
                    alignment: this.alignment,
                    textPart: this.textPart.getState("full"),
                    sectionWidth: this.sectionWidth,
                } as unknown as TableWidgetCellStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends TableWidgetCellFixState>(stateObj: TableWidgetCellStateMap[T], fixType: T) {
        switch (fixType) {
            case "props":
                this.UID = getStringProperty("UID", stateObj);
                this.alignment = getStringProperty("alignment", stateObj);
                this.textPart.loadState(stateObj.textPart, "full");
                this.setBackgroundColor(getColorProperty("backgroundColor", stateObj)); // лучше ставить после загрузки текста
                this.sectionWidth = getNumberProperty("sectionWidth", stateObj, 1);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                this.alignment = getStringProperty("alignment", stateObj);
                this.textPart.loadState(stateObj.textPart, "full");
                this.setBackgroundColor(getColorProperty("backgroundColor", stateObj)); // лучше ставить после загрузки текста
                this.sectionWidth = getNumberProperty("sectionWidth", stateObj, 1);
                break;
        }
    }

    UID = "";
    textPart: textWidget;
    height = 50;
    width = 200;
    startX = 0;
    startY = 0;

    sectionWidth = 1;

    backgroundColor = "#FFFFFF";
    alignment = "left";

    horizontalPadding = 20;
    verticalPadding = 20;
}
